import { Meal } from "@/clients/models/Meal";
import { SmileIcon } from "@/components/icons/smile-icon";
import ImageWithLoading from "@/components/image/image-with-loading";
import { cn } from "@/utils/cn";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { motion, PanInfo, useMotionValue, useTransform } from "motion/react";
import { useState } from "react";

interface TinderCardProps {
  meal: Meal;
  swipeThreshold: number;
  canSwipeLeft: boolean;
  canSwipeRight: boolean;
  onSwipe: (gesture: "left" | "right") => void;
}

export const TinderCard = ({
  meal,
  swipeThreshold,
  canSwipeLeft,
  canSwipeRight,
  onSwipe,
}: TinderCardProps) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 0, 200], [-15, 0, 15]);
  const dragY = useTransform(x, (value) => Math.abs(value) * 0.1);
  const [isExiting, setIsExiting] = useState(false);

  const triggerHapticFeedback = async () => {
    try {
      const canUseHaptics = Capacitor.getPlatform() !== "web";
      if (!canUseHaptics) return;

      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch {
      // Haptics not available, silently fail.
    }
  };

  const handleDragEnd = async (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const hasExceededSwipeThreshold = Math.abs(info.offset.x) > swipeThreshold;
    const gesture = info.offset.x < 0 ? "left" : "right";
    const canSwipe = gesture === "left" ? canSwipeLeft : canSwipeRight;

    if (hasExceededSwipeThreshold && canSwipe) {
      await triggerHapticFeedback();
      setIsExiting(true);

      setTimeout(() => {
        onSwipe(gesture);
        setIsExiting(false);
      }, 300);
      return;
    }

    x.set(0);
  };

  return (
    <motion.div
      className="absolute top-0 left-0 h-full w-full"
      style={{
        x,
        y: !isExiting ? dragY : 0,
        rotate,
        zIndex: 3,
      }}
      drag={!isExiting ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragSnapToOrigin={false}
      dragElastic={0.7}
      onDragEnd={handleDragEnd}
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{
        scale: 1,
        opacity: isExiting ? 0 : 1,
        y: isExiting ? 50 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: isExiting ? 200 : 300,
        damping: isExiting ? 25 : 30,
        opacity: { duration: 0.3 },
      }}
    >
      <div
        className={cn("relative h-full w-full overflow-hidden rounded-2xl bg-white", {
          "cursor-grab active:cursor-grabbing": !isExiting,
        })}
      >
        <ImageWithLoading
          src={meal.imageUri ?? ""}
          alt={meal.name}
          classNames={{
            container: "!h-full !w-full !bg-transparent",
            image: "h-full w-full object-cover",
          }}
          errorImage={<SmileIcon className="h-16.5 w-16.5" />}
        />

        <div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/60 to-transparent p-4">
          <h3 className="line-clamp-2 text-lg font-semibold text-white">{meal.name}</h3>
        </div>
      </div>
    </motion.div>
  );
};
